import { useValue } from "@legendapp/state/react";
import { Icon } from "@repo/ui/components/icon";
import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/ui/tooltip";
import type { ContextMenuItem } from "@repo/ui/ui/context-menu";
import type { DropdownMenuItem } from "@repo/ui/ui/dropdown-menu";
import { formatDistanceToNow } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import {
  datasets$,
  datasetsLocal$,
  useRunProgressLabel,
} from "~/stores/datasets";
import {
  getPayloadRefreshRetryAfterMs,
  refreshDatasetPayload,
} from "~/stores/datasets/datasets.actions";

/**
 * "Refresh dataset" entry shared by the block and dataset options menus.
 * Refresh scope is dataset-global, so every block on that dataset converges on
 * the new run.
 *
 * Keep this inside a menu popup: it owns a 1s countdown interval plus dataset
 * subscriptions, and Base UI only mounts popup contents while the menu is open.
 * Rendering it eagerly would put a timer behind every row with an options menu.
 */
export const DatasetRefreshMenuItem = ({
  datasetId,
  MenuItem,
  side = "top",
  sideOffset = 6,
}: {
  /** `null` renders the item disabled — a block can exist without a dataset. */
  datasetId: string | null | undefined;
  MenuItem: typeof DropdownMenuItem | typeof ContextMenuItem;
  side?: "top" | "right" | "bottom" | "left";
  sideOffset?: number;
}) => {
  const [nowMs, setNowMs] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1_000);
    return () => clearInterval(id);
  }, []);

  const fetchedAt = useValue(() =>
    datasetId ? datasets$[datasetId].fetchedAt.get() : null,
  );
  const status = useValue(() =>
    datasetId ? datasets$[datasetId].status.get() : "IDLE",
  );
  const manualRefreshPending = useValue(() =>
    datasetId
      ? (datasetsLocal$[datasetId].manualRefreshPending.get() ?? false)
      : false,
  );
  const retryAfterMs = useValue(() =>
    datasetId ? getPayloadRefreshRetryAfterMs(datasetId, nowMs) : 0,
  );
  const progressLabel = useRunProgressLabel(datasetId);

  const lastRefetchedLabel = useMemo(() => {
    if (!fetchedAt) return "Never";
    return formatDistanceToNow(new Date(fetchedAt), { addSuffix: true });
  }, [fetchedAt]);

  const isRefreshing = status === "RUNNING" || manualRefreshPending;
  const isRateLimited = retryAfterMs > 0;
  const canRefresh = !!datasetId && !isRefreshing && !isRateLimited;

  const handleRefresh = () => {
    if (!datasetId) return;
    void refreshDatasetPayload(datasetId).then((result) => {
      if (result.status === "THROTTLED") {
        const seconds = Math.max(1, Math.ceil(result.retryAfterMs / 1_000));
        toast.info(`Data was refreshed recently — try again in ${seconds}s.`);
      }
    });
  };

  return (
    <Tooltip>
      <MenuItem
        onClick={handleRefresh}
        disabled={!canRefresh}
        nativeButton
        render={(props) => (
          <TooltipTrigger
            {...props}
            className={[props.className, "flex w-full"]
              .filter(Boolean)
              .join(" ")}
          />
        )}
      >
        <Icon name="arrows-clockwise-repeat" className="size-4" />
        {isRefreshing ? "Refreshing dataset..." : "Refresh dataset"}
      </MenuItem>
      <TooltipContent
        side={side}
        align="start"
        sideOffset={sideOffset}
        className="flex flex-col items-start gap-1"
      >
        <div>Last refetched: {lastRefetchedLabel}</div>
        {!datasetId ? <div>No dataset connected</div> : null}
        {isRefreshing ? (
          <div>{progressLabel ?? "Refresh in progress..."}</div>
        ) : null}
        {isRateLimited ? (
          <div>Retry in {Math.max(1, Math.ceil(retryAfterMs / 1_000))}s</div>
        ) : null}
      </TooltipContent>
    </Tooltip>
  );
};
